// frontend/src/pages/StudentTopicContentDesktop.js
import React, { useEffect, useState } from 'react';
import ContentBlockRenderer, { resolveImageUrl, stripHtml } from './ContentBlockRenderer';
import AskAiPanel, { getStudentId } from './AskAiPanel';
import { API_BASE,authFetch } from '../services/api.js';
import './studenttopiccontentnew.css';
import { reportProgress, ConfidenceStrip, FlagChip } from './ProgressWidgets';
import { useLanguage } from '../contexts/languagecontext';


const GRADIENTS = [
    ['#4f46e5', '#7c3aed'],
    ['#0e7490', '#059669'],
    ['#b45309', '#dc2626'],
    ['#be185d', '#7c3aed'],
    ['#1d4ed8', '#0e7490'],
];
const gradientFor = (text) => {
    let h = 0;
    const s = text || '';
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
    return GRADIENTS[h % GRADIENTS.length];
};


const StudentTopicContentDesktop = ({
    topicId,
    language,
    topicName,
    onBack,
    error,
    loading,
    cards,
    expandedIndex,
    expandedContent,
    contentLoading,
    toggleCard,
    goToIndex,
    fullscreenMedia,
    openFullscreen,
    closeFullscreen,
}) => {
    useEffect(() => {
        if (!fullscreenMedia) return;
        window.history.pushState({ stcdFullscreen: true }, '');
        const handlePopState = () => closeFullscreen();
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullscreenMedia]);

    // ── Ask AI (admin-configurable; hidden unless enabled) ──
    const [askAiConf, setAskAiConf] = useState(null);
    const [askAiTarget, setAskAiTarget] = useState(null);
    const { hideImages, toggleImages } = useLanguage();
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const sid = getStudentId();
                const res = await fetch(`${API_BASE}/ask-ai/config/topic/${topicId}${sid ? `?student_id=${sid}` : ''}`);
                if (res.ok) {
                    const d = await res.json();
                    if (!cancelled) setAskAiConf(d);
                }
            } catch { /* feature stays hidden on failure */ }
        })();
        return () => { cancelled = true; };
    }, [topicId]);

    // ── Progress (desktop): map + optimistic status updates ──
    const [progress, setProgress] = useState({});
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const sid = getStudentId();
            if (!sid) return;
            try {
                const res = await authFetch(`${API_BASE}/progress/topic/${topicId}?student_id=${sid}`);
                if (res.ok) { const d = await res.json(); if (!cancelled) setProgress(d); }
            } catch { /* optional */ }
        })();
        return () => { cancelled = true; };
    }, [topicId]);

    const markProgress = (card, status) => {
        if (!card) return;
        reportProgress(topicId, card, { status });
        setProgress((prev) => {
            const key = `${card.card_type}-${card.card_id}`;
            const cur = prev[key] || {};
            if (cur.status === 'completed') return prev;
            return { ...prev, [key]: { ...cur, status } };
        });
    };

    // viewed whenever a card is opened
    useEffect(() => {
        if (expandedIndex !== null && cards[expandedIndex]) markProgress(cards[expandedIndex], 'viewed');
    }, [expandedIndex]);   // eslint-disable-line react-hooks/exhaustive-deps

    const activeCard = expandedIndex !== null ? cards[expandedIndex] : null;

    return (
        <div className="stcd-container">
            <aside className="stcd-sidepanel">
                <div className="stcd-sidepanel-header">
                    <h2>{topicName}</h2>
                    <button
                        className="star-imgtoggle-btn"
                        onClick={toggleImages}
                        title={hideImages ? 'Show images' : 'Hide images'}
                        aria-label={hideImages ? 'Show images' : 'Hide images'}
                    >
                        {hideImages ? '🖼️' : '🚫'}
                    </button>
                    {askAiConf?.enabled && (
                        <button className="star-askai-btn" onClick={() => setAskAiTarget({ card: null, block: null })} title="Ask AI">✦</button>
                    )}
                </div>

                {error && <div className="stcd-error">{error}</div>}

                {loading ? (
                    <p className="stcd-loading">Loading...</p>
                ) : (
                    <div className="stcd-list">
                        {cards.map((card, idx) => (
                            <div
                                key={idx}
                                className={`stcd-list-item ${expandedIndex === idx ? 'stcd-list-item-active' : ''}`}
                                onClick={() => toggleCard(idx)}
                            >
                                {card.feature_image_url ? (
                                    <img src={resolveImageUrl(card.feature_image_url)} alt="" className="stcd-list-thumb" />
                                ) : (
                                    <div
                                        className="stcd-list-thumb stcd-list-thumb-gradient"
                                        style={{ background: `linear-gradient(120deg, ${gradientFor(card.parent_heading || topicName)[0]}, ${gradientFor(card.parent_heading || topicName)[1]})` }}
                                    >
                                        {(stripHtml(card.heading) || '?').charAt(0)}
                                    </div>
                                )}
                                <div className="stcd-list-text">
                                    <h4>{card.parent_heading && <span className="stcd-eyebrow-inline-list">&gt; </span>}{stripHtml(card.heading) || '(untitled)'}</h4>
                                    {card.short_summary && <p className="stcd-list-summary">{card.short_summary}</p>}
                                    {progress[`${card.card_type}-${card.card_id}`]?.status === 'completed' && <span className="stf-done" style={{ fontSize: '0.75rem' }}>✓ Done</span>}
                                    {progress[`${card.card_type}-${card.card_id}`]?.status === 'viewed' && <span className="stf-inprogress" style={{ fontSize: '0.75rem' }}>In progress</span>}
                                </div>
                            </div>
                        ))}
                        {cards.length === 0 && <p className="stcd-empty">No content available yet.</p>}
                    </div>
                )}
            </aside>

            <main className="stcd-mainpane">
                {onBack && <button className="stcd-back-btn-main" onClick={onBack}>&larr; Back</button>}
                {!activeCard ? (
                    <div className="stcd-placeholder">
                        <p>Select a topic from the list to begin.</p>
                    </div>
                ) : (
                    
                    <div className="stcd-mainpane-content">
                        <div className="stcd-heading-row">
                            {activeCard.parent_heading && <span className="stcd-eyebrow-inline">{activeCard.parent_heading} &gt;</span>}
                            <h3>{stripHtml(activeCard.heading) || '(untitled)'}</h3>
                            <FlagChip topicId={topicId} card={activeCard} language={language} initial={progress[`${activeCard.card_type}-${activeCard.card_id}`]?.flag || null} />
                        </div>

                        {!hideImages && activeCard.feature_image_url && !activeCard.feature_image_is_fallback && (
                            <img
                                src={resolveImageUrl(activeCard.feature_image_url)}
                                alt=""
                                className="stc-modal-image"
                                onClick={() => openFullscreen({ type: 'image', url: resolveImageUrl(activeCard.feature_image_url), caption: '' })}
                            />
                        )}

                        {contentLoading ? (
                            <p className="stcd-loading">Loading...</p>
                        ) : expandedContent ? (
                            expandedContent.blocks.map((block, i) => (
                                <div key={i} className="stcd-block">
                                    <ContentBlockRenderer
                                        block={block}
                                        onMediaClick={openFullscreen}
                                        onAskAi={askAiConf?.enabled ? (b) => setAskAiTarget({ card: activeCard, block: b }) : undefined}
                                        askAiTypes={askAiConf?.block_types}
                                    />
                                </div>
                            ))
                        ) : (
                            <p>No content.</p>
                        )}

                        {expandedContent && !contentLoading && (
                            <ConfidenceStrip topicId={topicId} card={activeCard} language={language} initial={progress[`${activeCard.card_type}-${activeCard.card_id}`]?.confidence || null} />
                        )}

                        <div className="stcd-nav-buttons-floating">
                            <button onClick={() => goToIndex(expandedIndex - 1)} disabled={expandedIndex === 0}>&larr; Previous</button>
                            <button onClick={() => { markProgress(cards[expandedIndex], 'completed'); goToIndex(expandedIndex + 1); }} disabled={expandedIndex === cards.length - 1}>Next &rarr;</button>
                        </div>
                    </div>
                )}
            </main>

            {askAiTarget && (
                <AskAiPanel
                    topicId={topicId}
                    topicName={topicName}
                    card={askAiTarget.card}
                    block={askAiTarget.block}
                    language={language}
                    onClose={() => setAskAiTarget(null)}
                />
            )}

            {fullscreenMedia && (
                <div className="stcd-fullscreen">
                    <button className="stcd-fullscreen-back" onClick={() => window.history.back()}>&larr; Back</button>
                    <div className="stcd-fullscreen-content">
                        {fullscreenMedia.type === 'image' ? (
                            <img src={fullscreenMedia.url} alt={fullscreenMedia.caption || ''} />
                        ) : (
                            <div className="stcd-fullscreen-video">
                                <iframe src={fullscreenMedia.url} title="fullscreen-video" allowFullScreen />
                            </div>
                        )}
                        {fullscreenMedia.caption && <p className="stcd-fullscreen-caption">{fullscreenMedia.caption}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentTopicContentDesktop;